import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_ITEMS, NAV_CTA } from "../../constants/navigation";
import Container from "../ui/Container";
import Button from "../ui/Button";

export default function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const location = useLocation();

    const mobileMenuRef = useRef(null);
    const mobileToggleRef = useRef(null);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        setIsMobileOpen(false);
        setActiveDropdown(null);
    }, [location.pathname]);

    const closeMobile = useCallback(() => {
        setIsMobileOpen(false);
        mobileToggleRef.current?.focus();
    }, []);

    // Escape closes the mobile menu and any open dropdown, returning focus
    // to the control that opened them.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== "Escape") return;
            if (isMobileOpen) closeMobile();
            if (activeDropdown) setActiveDropdown(null);
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isMobileOpen, activeDropdown, closeMobile]);

    // Keep keyboard focus inside the mobile menu while it is open.
    useEffect(() => {
        if (!isMobileOpen) return;

        const handleTrap = (event) => {
            if (event.key !== "Tab" || !mobileMenuRef.current) return;

            const focusable = mobileMenuRef.current.querySelectorAll(
                'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusable.length === 0) return;

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleTrap);
        return () => document.removeEventListener("keydown", handleTrap);
    }, [isMobileOpen]);

    const isActive = (path) =>
        location.pathname === path || (path !== "/" && location.pathname.startsWith(path + "/"));

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                isScrolled ? "glass-strong shadow-lg" : "bg-transparent"
            }`}
        >
            <Container>
                <nav className="flex items-center justify-between h-20" aria-label="Main navigation">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-3 group">
                        <div className="w-10 h-10 rounded-xl overflow-hidden">
                            <img
                                src="/assets/branding/prodevop-logo-white-transparent-512x512.png"
                                alt="ProDevop"
                                className="w-full h-full object-contain"
                            />
                        </div>
                        <span className="font-display font-bold text-xl text-white group-hover:text-brand-400 transition-colors">
                            ProDevop
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-1">
                        {NAV_ITEMS.map((item) => (
                            <div
                                key={item.path}
                                className="relative"
                                onMouseEnter={() => item.children && setActiveDropdown(item.path)}
                                onMouseLeave={() => item.children && setActiveDropdown(null)}
                                // Closing on blur-out keeps the dropdown usable by keyboard:
                                // it stays open while focus is anywhere inside it.
                                onBlur={(event) => {
                                    if (item.children && !event.currentTarget.contains(event.relatedTarget)) {
                                        setActiveDropdown(null);
                                    }
                                }}
                            >
                                <Link
                                    to={item.path}
                                    onFocus={() => item.children && setActiveDropdown(item.path)}
                                    aria-expanded={item.children ? activeDropdown === item.path : undefined}
                                    aria-haspopup={item.children ? "true" : undefined}
                                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-body-sm font-medium transition-all duration-200 ${
                                        isActive(item.path)
                                            ? "text-brand-300 bg-brand-500/15 border border-brand-500/20 shadow-glow-purple-sm"
                                            : "text-zinc-300 hover:text-white hover:bg-white/5 border border-transparent"
                                    }`}
                                >
                                    {item.label}
                                    {item.children && <ChevronDown size={14} className="ml-0.5" aria-hidden="true" />}
                                </Link>

                                {/* Dropdown — opens on hover, on focus, and via an
                                    explicit keyboard-reachable toggle. */}
                                {item.children && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setActiveDropdown(activeDropdown === item.path ? null : item.path)
                                            }
                                            aria-expanded={activeDropdown === item.path}
                                            className="sr-only focus:not-sr-only focus:absolute focus:top-full focus:left-0 focus:z-10 focus:px-3 focus:py-1.5 focus:rounded-lg focus:bg-brand-500 focus:text-white focus:text-body-xs focus:whitespace-nowrap"
                                        >
                                            {activeDropdown === item.path ? "Close" : "Open"} {item.label} menu
                                        </button>

                                        <AnimatePresence>
                                            {activeDropdown === item.path && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: 8 }}
                                                    transition={{ duration: 0.2 }}
                                                    className="absolute top-full left-0 mt-2 w-72 py-2 rounded-xl glass-strong shadow-elevated"
                                                >
                                                    {item.children.map((child) => (
                                                        <Link
                                                            key={child.path}
                                                            to={child.path}
                                                            className="block px-4 py-2.5 text-body-sm text-zinc-300 hover:text-white hover:bg-white/5 focus:outline-none focus-visible:bg-white/10 focus-visible:text-white transition-colors"
                                                        >
                                                            {child.label}
                                                        </Link>
                                                    ))}
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Desktop CTA */}
                    <div className="hidden lg:flex items-center gap-3">
                        <Button variant="primary" size="sm" to={NAV_CTA.path}>
                            {NAV_CTA.label}
                        </Button>
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        ref={mobileToggleRef}
                        onClick={() => setIsMobileOpen(!isMobileOpen)}
                        className="lg:hidden p-2 rounded-lg text-zinc-300 hover:text-white hover:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 transition-colors"
                        aria-label={isMobileOpen ? "Close menu" : "Open menu"}
                        aria-expanded={isMobileOpen}
                        aria-controls="mobile-menu"
                    >
                        {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </nav>
            </Container>

            {/* Mobile Menu */}
            <AnimatePresence>
                {isMobileOpen && (
                    <motion.div
                        id="mobile-menu"
                        ref={mobileMenuRef}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden glass-strong border-t border-white/[0.06] overflow-hidden max-h-[calc(100vh-5rem)] overflow-y-auto"
                    >
                        <Container className="py-4">
                            <div className="flex flex-col gap-1">
                                {NAV_ITEMS.map((item) => (
                                    <div key={item.path}>
                                        <Link
                                            to={item.path}
                                            className={`block px-4 py-3 rounded-lg text-body-base font-medium transition-colors ${
                                                isActive(item.path)
                                                    ? "text-brand-300 bg-brand-500/10"
                                                    : "text-zinc-300 hover:text-white hover:bg-white/5"
                                            }`}
                                        >
                                            {item.label}
                                        </Link>
                                        {item.children && (
                                            <div className="ml-4 mt-1 flex flex-col gap-0.5">
                                                {item.children.map((child) => (
                                                    <Link
                                                        key={child.path}
                                                        to={child.path}
                                                        className="block px-4 py-2 rounded-lg text-body-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                                                    >
                                                        {child.label}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))}

                                <div className="mt-4 pt-4 border-t border-white/[0.06]">
                                    <Button variant="primary" size="md" to={NAV_CTA.path} className="w-full">
                                        {NAV_CTA.label}
                                    </Button>
                                </div>
                            </div>
                        </Container>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
